'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '../lib/supabase/client';
import type { PlanTier } from '@/lib/tierConfig';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: string;
  planTier: PlanTier;
  widgetToken: string | null;
  billingStatus: string;
  monthlyResolutionLimit: number;
  resolutionsUsed: number;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
}

export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  avatarUrl: string | null;
  role: string;
}

export interface OrgMember {
  id: string;
  organizationId: string;
  userId: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
}

interface AuthContextType {
  user: any;
  session: any;
  loading: boolean;
  userProfile: UserProfile | null;
  organization: Organization | null;
  orgMember: OrgMember | null;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  createOrganization: (name: string, tier: PlanTier) => Promise<Organization>;
  refreshOrganization: () => Promise<void>;
  hasFeature: (feature: string) => boolean;
  isOrgOwner: () => boolean;
  isOrgAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function generateWidgetToken(orgSlug: string): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = `es_${orgSlug.replace(/[^a-z0-9]/gi, '').slice(0, 8)}_`;
  for (let i = 0; i < 24; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

const TIER_RESOLUTION_LIMITS: Record<PlanTier, number> = {
  free: 150,
  growth: 1500,
  pro: 6000,
  enterprise: 999999,
};

const TIER_FEATURES: Record<string, PlanTier[]> = {
  basicWidget: ['free', 'growth', 'pro', 'enterprise'],
  integrations: ['free', 'growth', 'pro', 'enterprise'],
  advancedAnalytics: ['pro', 'enterprise'],
  multimodalReplies: ['pro', 'enterprise'],
  unlimitedUsers: ['pro', 'enterprise'],
  prioritySupport: ['growth', 'pro', 'enterprise'],
  whiteLabel: ['enterprise'],
  sla: ['enterprise'],
  customBranding: ['enterprise'],
  dedicatedOnboarding: ['pro', 'enterprise'],
  apiAccess: ['pro', 'enterprise'],
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [orgMember, setOrgMember] = useState<OrgMember | null>(null);
  const supabase = createClient();

  const fetchUserData = useCallback(async (userId: string) => {
    try {
      // Fetch user profile
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (profile) {
        setUserProfile({
          id: profile.id,
          email: profile.email,
          fullName: profile.full_name,
          avatarUrl: profile.avatar_url,
          role: profile.role,
        });
      }

      // Fetch org membership
      const { data: member } = await supabase
        .from('org_members')
        .select('*, organizations(*)')
        .eq('user_id', userId)
        .maybeSingle();

      if (member) {
        setOrgMember({
          id: member.id,
          organizationId: member.organization_id,
          userId: member.user_id,
          role: member.role,
        });

        const org = member.organizations as any;
        if (org) {
          setOrganization({
            id: org.id,
            name: org.name,
            slug: org.slug,
            plan: org.plan,
            planTier: (org.plan_tier || org.plan || 'free') as PlanTier,
            widgetToken: org.widget_token,
            billingStatus: org.billing_status || 'active',
            monthlyResolutionLimit: org.monthly_resolution_limit || 150,
            resolutionsUsed: org.resolutions_used || 0,
            stripeCustomerId: org.stripe_customer_id,
            stripeSubscriptionId: org.stripe_subscription_id,
          });
        }
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
    }
  }, [supabase]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserData(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserData(session.user.id).finally(() => setLoading(false));
      } else {
        setUserProfile(null);
        setOrganization(null);
        setOrgMember(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchUserData]);

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUserProfile(null);
    setOrganization(null);
    setOrgMember(null);
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data ? {
      id: data.id,
      email: data.email,
      fullName: data.full_name,
      avatarUrl: data.avatar_url,
      role: data.role,
    } : null;
  };

  const createOrganization = async (name: string, tier: PlanTier): Promise<Organization> => {
    if (!user) throw new Error('Must be logged in to create an organization');

    const baseSlug = slugify(name);
    const uniqueSlug = `${baseSlug}-${Date.now().toString(36)}`;
    const widgetToken = generateWidgetToken(uniqueSlug);
    const resolutionLimit = TIER_RESOLUTION_LIMITS[tier];

    // Create organization
    const { data: org, error: orgError } = await supabase
      .from('organizations')
      .insert({
        name,
        slug: uniqueSlug,
        plan: tier,
        plan_tier: tier,
        widget_token: widgetToken,
        monthly_resolution_limit: resolutionLimit,
        resolutions_used: 0,
        billing_status: 'active',
        owner_id: user.id,
      })
      .select()
      .single();

    if (orgError) throw orgError;

    // Add user as owner
    const { error: memberError } = await supabase
      .from('org_members')
      .insert({
        organization_id: org.id,
        user_id: user.id,
        role: 'owner',
      });

    if (memberError) throw memberError;

    const newOrg: Organization = {
      id: org.id,
      name: org.name,
      slug: org.slug,
      plan: org.plan,
      planTier: tier,
      widgetToken: org.widget_token,
      billingStatus: org.billing_status,
      monthlyResolutionLimit: org.monthly_resolution_limit,
      resolutionsUsed: org.resolutions_used,
      stripeCustomerId: org.stripe_customer_id,
      stripeSubscriptionId: org.stripe_subscription_id,
    };

    setOrganization(newOrg);
    setOrgMember({
      id: '',
      organizationId: org.id,
      userId: user.id,
      role: 'owner',
    });

    return newOrg;
  };

  const refreshOrganization = async () => {
    if (user) await fetchUserData(user.id);
  };

  const hasFeature = (feature: string): boolean => {
    if (!organization) return false;
    const allowedTiers = TIER_FEATURES[feature];
    if (!allowedTiers) return false;
    return allowedTiers.includes(organization.planTier);
  };

  const isOrgOwner = () => orgMember?.role === 'owner';
  const isOrgAdmin = () => orgMember?.role === 'owner' || orgMember?.role === 'admin';

  const value: AuthContextType = {
    user,
    session,
    loading,
    userProfile,
    organization,
    orgMember,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    createOrganization,
    refreshOrganization,
    hasFeature,
    isOrgOwner,
    isOrgAdmin,
  };

  return <AuthContext.Provider data-component-id="src/contexts/AuthContext.tsx:348:9" data-component-path="src/contexts/AuthContext.tsx" data-component-line="348" data-component-end-line="348" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>{children}</AuthContext.Provider>;
};
